// Step E — per-vehicle cylinder calibration modal.
//
// Once the cycle-fingerprint alignment (Steps A–D) has been
// bootstrapped, every recording's slots get rotation-aligned into
// template-frame "persistent IDs" P-A, P-B, P-C, … The IDs are
// opaque until the user assigns each one to a physical cylinder
// number — that's what this modal does.
//
// The user is the only one who knows which P-X corresponds to which
// engine cylinder. They make that mapping by:
//   - looking at a representative recording's per-slot stats
//     (spectrum thumb, phase, energy %),
//   - optionally doing something invasive in the engine bay (e.g.
//     temporarily disabling a cylinder, recording, observing which
//     P-X drops in energy),
//   - assigning each persistent slot to a physical cyl in the
//     dropdowns below.
//
// Partial assignments are allowed — the user can map only the
// cylinders they're sure about and come back later.

import { useEffect, useMemo, useState } from 'react';
import {
  api, Vehicle, parseCylAssignments,
} from './api';
import {
  AnalysisResult, CylinderHealthResult, PerCylinderMetrics,
} from './lib/ws';

interface Props {
  vehicle: Vehicle;
  onClose: () => void;
  onSaved: (next: Vehicle) => void;
}

export function CalibrateCylindersModal({ vehicle, onClose, onSaved }: Props) {
  const N = vehicle.num_cylinders;
  const initial = useMemo(() => parseCylAssignments(vehicle.persistent_cyl_assignments), [vehicle]);
  const [assignments, setAssignments] = useState<Record<number, number | ''>>(() => {
    // 0..N-1 → physical cyl OR '' for unassigned.
    const out: Record<number, number | ''> = {};
    for (let s = 0; s < N; s += 1) out[s] = initial[s] ?? '';
    return out;
  });
  const [latestRecording, setLatestRecording] = useState<{
    id: number;
    cylinders: CylinderHealthResult;
    rotation: number;
    confidence: number;
    template_count: number;
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Pull the most-recent analyzed recording for this vehicle that has
  // cycle-fingerprint data. NOT just the newest "done" — older
  // recordings may have been re-analyzed under the current pipeline
  // (so they have fingerprints) while the newest may pre-date the
  // pipeline and lack them. Walk the candidates newest-first and pick
  // the first one whose analysis_json contains an alignment.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get<{ recordings: Array<{
      id: number;
      vehicle_id: number | null;
      analysis_status: string;
      analysis_json: string | null;
      created_at: number;
    }> }>(`/api/recordings`)
      .then((res) => {
        if (cancelled) return;
        const candidates = res.recordings
          .filter((r) =>
            r.vehicle_id === vehicle.id
            && r.analysis_status === 'done'
            && r.analysis_json != null,
          )
          .sort((a, b) => b.created_at - a.created_at);
        for (const candidate of candidates) {
          if (!candidate.analysis_json) continue;
          let parsed: AnalysisResult;
          try { parsed = JSON.parse(candidate.analysis_json) as AnalysisResult; }
          catch { continue; }
          const cyl = parsed.components?.cylinders;
          const align = cyl?.cycle_fingerprint_alignment;
          if (!cyl || !align) continue;
          setLatestRecording({
            id: candidate.id,
            cylinders: cyl,
            rotation: align.rotation,
            confidence: align.confidence,
            template_count: align.template_count,
          });
          return;
        }
        // No candidate had fingerprint data.
        setLatestRecording(null);
      })
      .catch((e) => { if (!cancelled) setError((e as Error).message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [vehicle.id]);

  // For each template slot s, find the per-slot row from the most
  // recent recording: it lives at recording-frame slot
  // (s + rotation) mod N (because rotation says "template-slot s came
  // from recording-slot (s + rotation) mod N").
  const slotsView = useMemo(() => {
    const rows: Array<{
      slot: number;
      letter: string;
      perCyl: PerCylinderMetrics | null;
    }> = [];
    for (let s = 0; s < N; s += 1) {
      let perCyl: PerCylinderMetrics | null = null;
      if (latestRecording && N > 0) {
        const recIdx = ((s + latestRecording.rotation) % N + N) % N;
        perCyl = latestRecording.cylinders.cylinders.find(
          (c) => c.slot_index === recIdx,
        ) ?? null;
      }
      rows.push({ slot: s, letter: String.fromCharCode(65 + s), perCyl });
    }
    return rows;
  }, [N, latestRecording]);

  // Validate: no two assigned values may be equal.
  const duplicates = useMemo(() => {
    const seen = new Map<number, number[]>();
    for (const [slotKey, val] of Object.entries(assignments)) {
      if (val === '' || val == null) continue;
      const num = Number(val);
      const slot = Number(slotKey);
      const arr = seen.get(num) ?? [];
      arr.push(slot);
      seen.set(num, arr);
    }
    const dups = new Set<number>();
    for (const [, slots] of seen) {
      if (slots.length > 1) for (const s of slots) dups.add(s);
    }
    return dups;
  }, [assignments]);

  async function save() {
    setBusy(true);
    setError(null);
    try {
      const body: Record<string, number> = {};
      for (const [slot, cyl] of Object.entries(assignments)) {
        if (cyl !== '' && cyl != null) body[slot] = Number(cyl);
      }
      const updated = await api.put<{ id: number; persistent_cyl_assignments: string | null }>(
        `/api/vehicles/${vehicle.id}/persistent-cyls`,
        { assignments: Object.keys(body).length === 0 ? null : body },
      );
      onSaved({ ...vehicle, persistent_cyl_assignments: updated.persistent_cyl_assignments });
      onClose();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  function reset() {
    const next: Record<number, number | ''> = {};
    for (let s = 0; s < N; s += 1) next[s] = '';
    setAssignments(next);
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div
        className="modal"
        style={{ maxWidth: 720, width: '95%' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <h3 style={{ margin: 0 }}>Cylinder calibration — {vehicle.name}</h3>
          <button className="modal-close" onClick={onClose} aria-label="close">×</button>
        </div>

        <div className="muted" style={{ fontSize: '0.85rem', marginBottom: 8 }}>
          Each row is a "persistent" cylinder (P-A, P-B…) the app has
          identified as the same physical cylinder across recordings.
          Use the dropdown to map each one to the engine cylinder number
          you actually want it labeled as. <strong>You don't have to
          assign all of them right away</strong> — partial maps are
          fine, you can come back later.
          {N > 0 && latestRecording && (
            <>
              {' '}Stats from clip <strong style={{ color: 'var(--fg)' }}>#{latestRecording.id}</strong>;
              rotation r={latestRecording.rotation},
              alignment confidence {(latestRecording.confidence * 100).toFixed(0)}%,
              template folds {latestRecording.template_count} clip{latestRecording.template_count === 1 ? '' : 's'}.
            </>
          )}
        </div>

        {loading && <div className="muted">loading latest recording…</div>}
        {!loading && !latestRecording && (
          <div className="muted" style={{ padding: '0.5rem 0' }}>
            No analyzed recording yet for this vehicle, or alignment hasn't
            bootstrapped. Record a clip first; once it analyzes, persistent
            IDs are populated and the rows below will show per-slot stats.
            (Mapping still works without stats — just less context.)
          </div>
        )}

        <table style={{ width: '100%', fontSize: '0.85rem', marginTop: 6 }}>
          <thead>
            <tr style={{ color: 'var(--muted)', borderBottom: '1px solid var(--border)' }}>
              <th style={{ textAlign: 'left' }}>Persistent</th>
              <th style={{ textAlign: 'left' }}>Spectrum</th>
              <th style={{ textAlign: 'right' }}>Energy %</th>
              <th style={{ textAlign: 'right' }}>Phase</th>
              <th style={{ textAlign: 'left', paddingLeft: 16 }}>→ Physical cyl</th>
            </tr>
          </thead>
          <tbody>
            {slotsView.map(({ slot, letter, perCyl }) => {
              const dup = duplicates.has(slot);
              return (
                <tr key={slot} style={{ borderBottom: '1px solid var(--border)' }}>
                  <td style={{ padding: '4px 0' }}>
                    <strong>P-{letter}</strong>
                    {perCyl?.health && (
                      <span style={{
                        marginLeft: 6, fontSize: '0.7rem',
                        color: perCyl.health === 'red' ? 'var(--danger)'
                             : perCyl.health === 'yellow' ? '#f59e0b'
                             : 'var(--ok)',
                      }}>● {perCyl.health}</span>
                    )}
                  </td>
                  <td style={{ padding: '4px 0' }}>
                    {perCyl ? <ThumbBar thumb={perCyl.spectrum_thumb} /> : <span className="muted">—</span>}
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    {perCyl != null
                      ? `${perCyl.energy_pct_of_cohort.toFixed(0)}%`
                      : '—'}
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    {perCyl != null
                      ? `${perCyl.phase_offset_deg.toFixed(1)}°`
                      : '—'}
                  </td>
                  <td style={{ paddingLeft: 16 }}>
                    <select
                      value={assignments[slot] ?? ''}
                      onChange={(e) => {
                        const v = e.target.value;
                        setAssignments((prev) => ({
                          ...prev,
                          [slot]: v === '' ? '' : Number(v),
                        }));
                      }}
                      disabled={busy}
                      style={{
                        minWidth: 160,
                        borderColor: dup ? 'var(--danger)' : undefined,
                      }}
                    >
                      <option value="">— unassigned —</option>
                      {Array.from({ length: N }, (_, i) => i + 1).map((cyl) => (
                        <option key={cyl} value={cyl}>Cyl {cyl}</option>
                      ))}
                    </select>
                    {dup && (
                      <div style={{ color: 'var(--danger)', fontSize: '0.7rem' }}>
                        duplicate
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        {error && <div className="error" style={{ marginTop: 8 }}>{error}</div>}

        <div className="row" style={{ marginTop: 12, justifyContent: 'space-between' }}>
          <button
            className="secondary"
            onClick={reset}
            disabled={busy}
          >Reset all</button>
          <div className="row" style={{ gap: 6 }}>
            <button className="secondary" onClick={onClose} disabled={busy}>Cancel</button>
            <button
              onClick={save}
              disabled={busy || duplicates.size > 0}
              title={duplicates.size > 0 ? 'Resolve duplicate assignments first' : ''}
            >Save</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---- 32-bin spectrum thumbnail (small SVG bar chart) ----------------

function ThumbBar({ thumb }: { thumb: number[] }) {
  const W = 110;
  const H = 22;
  if (!thumb || thumb.length === 0) return <span className="muted">—</span>;
  const max = Math.max(...thumb, 1e-12);
  return (
    <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none"
         style={{ width: W, height: H, display: 'block' }}>
      {thumb.map((v, i) => {
        const x = (i / thumb.length) * W;
        const bw = W / thumb.length;
        const h = (v / max) * H;
        return (
          <rect
            key={i}
            x={x} y={H - h}
            width={Math.max(0.5, bw - 0.4)}
            height={h}
            fill="var(--accent)"
            opacity={0.85}
          />
        );
      })}
    </svg>
  );
}
